import { useEffect, useRef, useState } from 'react';
import { LowLevelAuthFlowConfig } from '../components/LowLevelAuthFlowView/LowLevelAuthFlowMachineDefinition';
import { AuthApiPayload, OAuthParamsV2 } from '../types';
import { captureException } from '../../internal/ravenUtils';

export type AuthConfigFetchState<AuthRequest, AuthResponse> =
  | {
      status: 'succeeded';
      config: LowLevelAuthFlowConfig<AuthRequest, AuthResponse>;
    }
  | { status: 'pending' }
  | {
      status: 'failed';
      err?: Error;
    };

export function useAuthConfigFetch<AuthResponse>(
  fetchAuthConfig: () => Promise<
    LowLevelAuthFlowConfig<AuthApiPayload<OAuthParamsV2>, AuthResponse>
  >
): AuthConfigFetchState<AuthApiPayload<OAuthParamsV2>, AuthResponse> {
  const [configFetchState, setConfigFetchState] = useState<
    AuthConfigFetchState<AuthApiPayload<OAuthParamsV2>, AuthResponse>
  >({ status: 'pending' });

  const fetchAuthConfigRef = useRef(fetchAuthConfig);
  // eslint-disable-next-line react-hooks/refs
  fetchAuthConfigRef.current = fetchAuthConfig;

  useEffect(() => {
    let cancelled = false;

    fetchAuthConfigRef
      .current()
      .then((authConfig) => {
        if (!cancelled) {
          setConfigFetchState(() => ({
            status: 'succeeded',
            config: authConfig,
          }));
        }
      })
      .catch((err: Error) => {
        captureException(
          new Error('Failed to fetch auth config', { cause: err })
        );
        if (!cancelled) {
          setConfigFetchState(() => ({ status: 'failed', err }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return configFetchState;
}
